import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { LanguageService } from './modules/shared/services/language.service';
import { AuthService } from './modules/shared/services/auth.service';
import { TenantsService } from './modules/shared/services/tenants.service';
import { ThemeService } from './modules/shared/services/theme.service';
import { NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AuthenticationComponent } from './modules/shared/authentication/authentication.component';
import { WorkAreaContainerComponent } from './modules/shared/components/work-area-container/work-area-container.component';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  variables: any;
  title = 'Estimator';
  showLayout = false;
  private destroy$ = new Subject<void>();
  private currentRole: string | null = null;
  private currentUrl = '';
  private lastAppliedTenantId: string | null = null;

  constructor(
    private languageService: LanguageService,
    public authservice: AuthService,
    private tenantsService: TenantsService,
    private themeService: ThemeService,
    private router: Router,
    private titleService: Title,
  ) { }

  @HostListener('document:keydown', ['$event'])
  handleHardRefresh(event: KeyboardEvent): void {
    // Check if Ctrl (or Cmd on Mac) + F5 is pressed
    // F5 keyCode is 116
    if ((event.ctrlKey || event.metaKey) && event.keyCode === 116) {
      // Note: The browser will still proceed with the refresh after this
    }
  }
  ngOnInit(): void {
    // 1. Apply branding for the current tenant id in the URL path (before login, no auth needed)
    this.themeService.loadByTenantId().pipe(takeUntil(this.destroy$)).subscribe();

    this.currentUrl = this.router.url;
    this.titleService.setTitle(this.title);

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.urlAfterRedirects;
          this.updateBrowserTitle();
        }
      });

    this.languageService.translations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vars: any) => {
        this.variables = vars;
        this.updateBrowserTitle();
      });

    // 2. After login: load tenant branding from /tenants/current and apply it
    this.authservice.getUserData()
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user)
      )
      .subscribe((user) => {
        this.currentRole = user?.role ?? null;
        this.updateBrowserTitle();

        // Avoid redundant calls if tenant hasn't changed
        const tenantId = user?.tenantId ?? null;
        if (tenantId && tenantId === this.lastAppliedTenantId) return;
        this.lastAppliedTenantId = tenantId;

        this.tenantsService.getCurrent().pipe(
          takeUntil(this.destroy$)
        ).subscribe({
          next: branding => this.themeService.applyBranding(branding),
          error: () => { /* keep path-based branding if /current fails */ }
        });
      });
  }

  private updateBrowserTitle(): void {
    if (this.currentUrl.startsWith('/login')) {
      this.titleService.setTitle(this.title);
      return;
    }

    if (!this.currentRole) {
      this.titleService.setTitle(this.title);
      return;
    }

    const roleLabels: { [key: string]: string | undefined } = {
      'SUPER-ADMIN': this.variables?.superAdmin,
      'ADMIN': this.variables?.admin,
      'USER': this.variables?.normalUser,
      'ARCHITECT': this.variables?.architect,
      'ESTIMATION-OFFICE': this.variables?.estimationOffice,
    };

    const translatedRole = roleLabels[this.currentRole] || this.currentRole;
    this.titleService.setTitle(`${this.title} (${translatedRole})`);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
